/**
 * WorkshopOrdersPage — Phase 2b Werkstatt-Aufträge.
 *
 * Status-Workflow:
 *   draft → quote → confirmed → in_progress → completed → invoiced
 *                                              cancelled (terminal)
 *
 * Lagerteile werden beim Übergang in "completed" automatisch abgebucht.
 * Bei "completed" kann der Auftrag in eine §14 UStG-Rechnung umgewandelt werden.
 *
 * Aufruf: /dealer/workshop-orders (Header-Link für B2B sichtbar)
 */
import { confirmDialog } from "../lib/confirmDialog";
import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
    Plus, Wrench, X, Edit2, Trash2, ArrowRight, FileText,
    Check, AlertTriangle, Car, ClipboardList, Package, Clock, Download, Mail, Bell, QrCode, Play, Square, Share2, Timer,
    CalendarClock, Phone, ArrowRightCircle, CheckCircle2, XCircle, ChevronRight, Camera, Search, Truck, ShieldCheck, Building2, Users, Bike, Zap, CalendarCheck,
} from "lucide-react";

import api, { setApiOrgBranch, getApiEmployer, setApiEmployer } from "../lib/api";
import { useAuth } from "../lib/auth";
import { SettingsShortcut } from "../components/dealer/SettingsShortcut";
import WorkshopBranchSwitcher from "../components/workshop/WorkshopBranchSwitcher";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import CustomerPickerForOrder from "../components/workshop-orders/CustomerPickerForOrder";
import MobileZb1QrCard from "../components/workshop-orders/MobileZb1QrCard";
import TimesheetModal from "../components/workshop-orders/TimesheetModal";
// Jul-2026 (User-Wunsch) — Rechnungs-Vorschau vor der Rechnungserstellung
import InvoicePreviewModal from "../components/workshop/InvoicePreviewModal";
// Phase 4g.C (Feb 2026) — OrderCard + Konstanten in eigene Modul-Datei
// extrahiert, damit WorkshopOrdersPage.jsx unter 1500 Zeilen bleibt und die
// Auftragskarte separat snapshot-testbar ist.
import OrderCard from "../components/workshop/OrderCard";
// Phase 4g.C+ (Feb 2026) — WorkOrderModal (Edit-Dialog mit Positions-CRUD,
// Schadensfotos, Live-Totals) ebenfalls extrahiert. Halbiert die Page-Größe.
import WorkOrderModal from "../components/workshop/WorkOrderModal";
// Phase 4g.E (Feb 2026) — finale Helper-Modals + Bookings-Tab extrahiert,
// damit die Page jetzt unter 500 Zeilen liegt und sich auf reine Listen-
// und Status-Transition-Logik konzentriert.
import SendQuoteMailModal from "../components/workshop/SendQuoteMailModal";
import QrPreviewModal from "../components/workshop/QrPreviewModal";
import ShareOrderModal from "../components/workshop/ShareOrderModal";
import { SharedOrdersBoard } from "../components/workshop/SharedOrdersBoard";
import BookingsTab from "../components/workshop/BookingsTab";
// Jun-2026 (User-Wunsch) — Miet-/Werkstattersatzwagen-Bereich
import RentalVehiclesTab from "../components/workshop/RentalVehiclesTab";
import OpenDeliveryOrdersTab from "../components/workshop-orders/OpenDeliveryOrdersTab";
import OpenWarrantyOrdersTab from "../components/workshop-orders/OpenWarrantyOrdersTab";
// Jun-2026 (User-Wunsch) — Kunden- & Fahrzeuge-Tab auch hier (wiederverwendbar)
import CustomersPanel from "../components/crm/CustomersPanel";
import VehiclesPanel from "../components/crm/VehiclesPanel";
import EmployeeContextBanner from "../components/vermittler/EmployeeContextBanner";
import { clearEmployerContext } from "../lib/employerModules";
import { STATUS_META, fmtEur, ORDER_CATEGORY_LIST, ORDER_CATEGORY_META, orderCategories } from "../components/workshop/workshopConstants";
import { previewPdfResponse, customerRecipient } from "../lib/pdfPreview";

export default function WorkshopOrdersPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [prefillPositions, setPrefillPositions] = useState(null);
    const [orders, setOrders] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState("");
    const [filterCategory, setFilterCategory] = useState("");
    const [filterOutsideHours, setFilterOutsideHours] = useState(false);
    const [filterIncomplete, setFilterIncomplete] = useState(false);
    const [modalOpen, setModalOpen] = useState(null); // {} new | {id...} edit | null
    const [mailTarget, setMailTarget] = useState(null);  // {order, customer} | null
    const [qrTarget, setQrTarget] = useState(null);  // order | null
    const [shareTarget, setShareTarget] = useState(null);  // order | null
    const [timerTarget, setTimerTarget] = useState(null);  // order | null
    const [invoicePreview, setInvoicePreview] = useState(null); // order | null — Rechnungs-Vorschau (Jul-2026)
    const [billingBusy, setBillingBusy] = useState(false);
    const [highlightId, setHighlightId] = useState("");
    const [employees, setEmployees] = useState([]);
    const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
    const [pickerInitialTab, setPickerInitialTab] = useState("existing");
    const [pickerPreloadedScan, setPickerPreloadedScan] = useState(null); // Handoff-Bündel {documents, match}
    const [filterEmployee, setFilterEmployee] = useState("");
    // Phase 4g.Q — Universal-Suchfeld (Kunden + Fahrzeug + Auftragsdaten)
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    // Phase 4c++ — Tab-Integration: eingehende Online-Buchungen
    const [activeTab, setActiveTab] = useState(() => {
        const t = searchParams.get("tab");
        return ["bookings", "rental", "delivery-open", "warranty-open", "customers", "vehicles"].includes(t) ? t : "orders";
    });
    const [bookings, setBookings] = useState([]);
    // Mehr-Standort Phase 4 — Werkstatt-Filial-Umschalter (Hauptsitz).
    const [orgBranch, setOrgBranch] = useState(() => sessionStorage.getItem("tw_ws_branch") || null);

    useEffect(() => {
        setApiOrgBranch(orgBranch);
        return () => setApiOrgBranch(null);
    }, [orgBranch]);

    // P1 Werkstatt (Jun-2026) — Mitarbeiter-Kontext (Vermittler im Firma-Bereich).
    const isEmployeeContext = !!(user && user.role === "vermittler" && getApiEmployer());
    const exitEmployerContext = useCallback(() => {
        setApiEmployer(null);
        clearEmployerContext();
        navigate("/vermittler");
    }, [navigate]);

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            // Jul-2026 (User-Wunsch „Zähler prüfen"): Status/Mitarbeiter/24H werden
            // CLIENT-seitig gefiltert — so zeigen die Filter-Chips immer die echten
            // Gesamt-Zahlen. Nur die Universal-Suche läuft serverseitig (durchsucht
            // auch Kundendaten org-weit).
            if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());
            const [o, c, a, emp, bk] = await Promise.all([
                api.get(`/work-orders?${params.toString()}`),
                api.get("/customers"),
                api.get("/articles"),
                api.get("/dealer/employees").catch(() => ({ data: { items: [] } })),
                api.get("/workshop/bookings").catch(() => ({ data: { items: [] } })),
            ]);
            setOrders(o.data || []);
            setCustomers(c.data || []);
            setArticles(a.data || []);
            setEmployees((emp.data?.items || []).filter((e) => e.is_active !== false));
            setBookings(bk.data?.items || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
        }
        setLoading(false);
    }, [filterStatus, filterEmployee, filterOutsideHours, debouncedSearch, orgBranch]); // eslint-disable-line react-hooks/exhaustive-deps

    // Phase 4g.Q — 300 ms-Debounce für das Universal-Suchfeld
    useEffect(() => {
        const h = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(h);
    }, [search]);

    useEffect(() => {
        if (user === false) navigate("/login");
        else if (user && user.role !== "b2b" && user.role !== "admin") {
            // Mitarbeiter (Vermittler) dürfen das echte Werkstatt-Modul nutzen,
            // sofern ein bestätigter Arbeitgeber-Kontext aktiv ist (X-Employer-Id).
            // Jun-2026 (Bugfix): Feste Mitarbeiter (zweiradverkaeufer) arbeiten
            // im Kontext ihres Arbeitgeber-Händlers — Backend mappt automatisch.
            const isFixedEmployee = user.role === "zweiradverkaeufer";
            if (!isFixedEmployee && !(user.role === "vermittler" && getApiEmployer())) navigate("/");
        }
        if (user) loadAll();
    }, [user, navigate, loadAll]);

    // Jun-2026 (User-Wunsch) — Handy-Handoff / 24/7-Selbstannahme: liegt ein
    // Auto-Entwurf vor (order_id), öffnen wir diesen direkt zur Prüfung; sonst
    // (ältere Sessions) der Scan-Picker.
    const handleHandoffBundle = useCallback(async (bundle) => {
        if (bundle?.order_id) {
            try {
                const { data } = await api.get(`/work-orders/${bundle.order_id}`);
                toast.success(`Selbstannahme ${bundle.order_number || ""} übernommen — bitte prüfen & freigeben`);
                loadAll();
                setModalOpen(data);
                return;
            } catch { /* fällt auf den Picker zurück */ }
        }
        setPickerPreloadedScan(bundle);
        setPickerInitialTab("scan");
        setCustomerPickerOpen(true);
    }, [loadAll]);


    const transition = async (id, status, note) => {
        try {
            await api.post(`/work-orders/${id}/status`, { status, note: note || null });
            toast.success(`Status → ${STATUS_META[status]?.label || status}`);
            if (status === "cancelled") toast.info("Der Filialleiter wurde über die Stornierung informiert und prüft den Vorgang.");
            loadAll();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    };

    // Jul-2026 (User-Wunsch) — EIN klarer Abrechnungs-Weg: „Rechnung erstellen"
    // öffnet zuerst die Vorschau aller Positionen („Alle Positionen enthalten?").
    const openInvoicePreview = (order) => {
        if (order.invoice_id) { toast.info("Auftrag wurde bereits abgerechnet"); return; }
        setInvoicePreview(order);
    };

    // Führt die eigentliche Abrechnung aus (nach Bestätigung in der Vorschau).
    // mode: "direct" (finalisieren + PDF) · "draft" (Entwurf → Rechnungs-Center)
    //       "split"  (Kundenrechnung + interne Folge-Aufträge)
    // Der Auftrag wird vom Backend automatisch abgeschlossen (Lagerteile abgebucht).
    const performBilling = async (order, mode, voucherCode = null, customTotalGross = null, paymentCode = null) => {
        setBillingBusy(true);
        const vbody = {
            ...(voucherCode ? { voucher_code: voucherCode } : {}),
            ...(customTotalGross ? { custom_total_gross: customTotalGross } : {}),
            ...(paymentCode ? { payment_terms_code: paymentCode } : {}),
        };
        try {
            if (mode === "split") {
                const res = await api.post(`/work-orders/${order.id}/bill-customer-split`, vbody);
                const fu = (res.data.follow_up_orders || [])
                    .map((f) => `${f.label} → ${f.order_number}`).join(" · ");
                toast.success(
                    `Abrechnung aufgeteilt: ${res.data.invoice ? "Kundenrechnung erstellt" : "keine Kundenrechnung"}`
                    + (fu ? ` · Folge-Aufträge: ${fu}` : ""));
                if (res.data.invoice) setTimeout(() => navigate("/dealer/invoices"), 1200);
            } else if (mode === "draft") {
                await api.post(`/work-orders/${order.id}/convert-to-invoice`, vbody);
                toast.success("Rechnungs-Entwurf erstellt → Rechnungs-Center");
                setTimeout(() => navigate("/dealer/invoices"), 800);
            } else {
                const conv = await api.post(`/work-orders/${order.id}/convert-to-invoice`, vbody);
                const invId = conv.data?.invoice_id;
                if (!invId) throw new Error("Rechnung konnte nicht erstellt werden");
                const fin = await api.post(`/invoices/${invId}/finalize`);
                const invNo = fin.data?.invoice_number || "";
                toast.success(`Rechnung ${invNo} erstellt & finalisiert`);
                const pdf = await api.get(`/invoices/${invId}/pdf`, { responseType: "blob" });
                previewPdfResponse(pdf, `Rechnung_${invNo || invId}.pdf`, { recipient: customerRecipient(await resolveCustForSend(order)) });
                setInvoicePreview(null);
                loadAll();
                setBillingBusy(false);
                // Aug-2026 (User-Wunsch): keine Rückfrage mehr — Rechnung wird
                // immer automatisch versendet bzw. in die Versand-Freigabe gelegt.
                try {
                    const sr = await api.post(`/invoices/${invId}/send-email`, {});
                    if (sr.data?.queued_for_approval) {
                        toast.success(`Rechnung ${invNo} an ${sr.data.sent_to} — wartet in der Versand-Freigabe`);
                    } else {
                        toast.success(sr.data?.mocked ? `Rechnung an ${sr.data.sent_to} protokolliert (MOCK — Mail-Versand noch nicht live)` : `Rechnung ${invNo} automatisch an ${sr.data.sent_to} gesendet`);
                    }
                } catch (se) {
                    const msg = se.response?.data?.detail || "";
                    if (msg.includes("Keine E-Mail-Adresse")) {
                        toast.info("Kunde hat keine E-Mail-Adresse — Rechnung wurde nicht versendet (PDF liegt vor)");
                    } else {
                        toast.error(msg || "E-Mail-Versand fehlgeschlagen");
                    }
                }
                return;
            }
            setInvoicePreview(null);
            loadAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message || "Rechnung konnte nicht erstellt werden");
            loadAll();
        }
        setBillingBusy(false);
    };

    // Phase 4c++ — Konvertierung Online-Buchung → Werkstatt-Auftrag
    const convertBookingToOrder = async (bookingId) => {
        if (!window.confirm("Buchung in vollwertigen Werkstatt-Auftrag umwandeln?\n\nKunde, Fahrzeug, Termin und Service-Type werden automatisch übernommen.")) return;
        try {
            const r = await api.post(`/workshop/bookings/${bookingId}/convert-to-order`);
            toast.success(`Auftrag ${r.data.work_order_number} erstellt`);
            setActiveTab("orders");
            setHighlightId(r.data.work_order_id);
            setTimeout(() => setHighlightId(""), 4000);
            loadAll();
        } catch (e) { toast.error(e.response?.data?.detail || "Konvertierung fehlgeschlagen"); }
    };

    const updateBookingStatus = async (bookingId, status) => {
        const labels = { confirmed: "bestätigt", completed: "erledigt", cancelled: "storniert" };
        if (status === "cancelled" && !window.confirm("Buchung wirklich stornieren?")) return;
        try {
            await api.patch(`/workshop/bookings/${bookingId}/status`, { status });
            toast.success(`Buchung ${labels[status] || "aktualisiert"}`);
            loadAll();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Auftrag wirklich endgültig löschen?\n\nBereits erfolgte Lagerbuchungen und Belege bleiben erhalten. Abgerechnete Aufträge können nicht gelöscht werden.")) return;
        try {
            await api.delete(`/work-orders/${id}`);
            toast.success("Auftrag gelöscht");
            loadAll();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    };

    // Jul-2026 (Bugfix): Kunde für Versand-Vorbefüllung (WhatsApp/SMS/E-Mail)
    // ggf. org-weit nachladen — er ist nicht immer in der geladenen Liste.
    const resolveCustForSend = async (order) => {
        if (custById[order.customer_id]) return custById[order.customer_id];
        if (order.customer_id) {
            try { return (await api.get(`/customers/${order.customer_id}`)).data; } catch { /* */ }
        }
        return order.customer_brief || null;
    };

    const downloadQuotePdf = async (order) => {
        try {
            const res = await api.get(`/work-orders/${order.id}/quote-pdf`, { responseType: "blob" });
            previewPdfResponse(res, `Kostenvoranschlag_${order.order_number || order.id}.pdf`, { recipient: customerRecipient(await resolveCustForSend(order)) });
        } catch (e) {
            toast.error("PDF konnte nicht erstellt werden (Auftrag braucht Positionen)");
        }
    };

    const sendReminderEmail = async (order) => {
        const force = order.reminder_sent_at && !window.confirm(
            "Erinnerung wurde bereits versendet. Erneut senden?",
        ) ? null : (order.reminder_sent_at ? "?force=true" : "");
        if (force === null) return;
        try {
            const res = await api.post(`/work-orders/${order.id}/send-reminder-email${force}`);
            if (res.data?.ok === false && res.data?.reason === "already_sent") {
                toast.info("Erinnerung bereits versendet.");
            } else {
                toast.success(res.data?.mocked ? "Erinnerung (MOCKED) protokolliert" : "Erinnerung versendet");
            }
            loadAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Versand fehlgeschlagen");
        }
    };

    // Jun-2026 (User-Wunsch) — Auftrag im Werkstatt-Kalender einplanen/anzeigen.
    // Bestätigte, unterminierte Aufträge werden beim Speichern des Termins
    // direkt gestartet („Einplanen & Start").
    const planInCalendar = (order) => {
        const startFlag = order.status === "confirmed" && !order.appointment_at ? "&start=1" : "";
        const param = order.appointment_at ? `focus=${order.id}` : `schedule=${order.id}${startFlag}`;
        navigate(`/dealer/workshop-calendar?${param}`);
    };

    // Jun-2026 (User-Wunsch) — „Rechnung direkt": ersetzt durch die
    // Rechnungs-Vorschau (openInvoicePreview + performBilling, Jul-2026).

    // Jun-2026 (User-Wunsch) — Kunde über überschrittenen Wunschtermin informieren.
    const notifyDelay = async (order) => {
        if (order.delay_notified_at && !window.confirm("Verzögerung wurde bereits gemeldet. Erneut informieren?")) return;
        const reason = window.prompt("Grund der Verzögerung (optional, wird dem Kunden mitgeteilt):", "") ?? "";
        try {
            const res = await api.post(`/work-orders/${order.id}/notify-delay`, { reason: reason.trim() });
            toast.success(res.data?.notification?.mocked ? "Kunde über Verzögerung informiert (MOCKED)" : "Kunde über Verzögerung informiert");
            loadAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Benachrichtigung fehlgeschlagen");
        }
    };

    const downloadQrSticker = async (order) => {
        try {
            const res = await api.get(`/work-orders/${order.id}/qr-sticker.pdf`, { responseType: "blob" });
            previewPdfResponse(res, `QR-Aufkleber_${order.order_number || order.id}.pdf`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "QR-Aufkleber konnte nicht erstellt werden");
        }
    };

    // Jul-2026 (User-Wunsch) — „Drucken": Browser-Druckdialog direkt öffnen
    // (autoPrint), Vorschau bleibt im Hintergrund.
    const printOrder = async (order) => {
        try {
            const res = await api.get(`/work-orders/${order.id}/print-bundle.pdf`, { responseType: "blob" });
            previewPdfResponse(res, `Werkstattdruck_${order.order_number || order.id}.pdf`, { autoPrint: true, recipient: customerRecipient(await resolveCustForSend(order)) });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Auftrag konnte nicht gedruckt werden");
        }
    };

    // Nachdruck — nur das QR-Code-Blatt (ohne die Auftragsseiten).
    const reprintBundle = async (order) => {
        try {
            const res = await api.get(`/work-orders/${order.id}/print-qr.pdf`, { responseType: "blob" });
            previewPdfResponse(res, `QR-Nachdruck_${order.order_number || order.id}.pdf`, { autoPrint: true });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Nachdruck fehlgeschlagen");
        }
    };

    // Phase 4 — Wenn ?focus={wid} gesetzt, Auftrag scrollen + Modal öffnen
    useEffect(() => {
        const focusId = searchParams.get("focus");
        if (!focusId || !orders.length) return;
        const order = orders.find((o) => o.id === focusId);
        if (order) {
            setHighlightId(focusId);
            // Jun-2026 (User-Wunsch) — Push-Deep-Link öffnet den Auftrag direkt
            // (mit Termin-Quick-Actions im Modal), nicht nur Scrollen.
            setModalOpen(order);
            setTimeout(() => {
                const el = document.querySelector(`[data-testid="wo-row-${focusId}"]`);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 100);
            // ?focus aus URL entfernen, damit Reload-Schleifen vermieden werden
            const next = new URLSearchParams(searchParams);
            next.delete("focus");
            setSearchParams(next, { replace: true });
        }
    }, [orders, searchParams, setSearchParams]);

    // Jun-2026 (User-Wunsch) — Deep-Links: ?action=new öffnet die Auftragsanlage,
    // ?status=open|<status> setzt den Statusfilter (z. B. aus Dashboard-Kachel).
    useEffect(() => {
        const action = searchParams.get("action");
        const status = searchParams.get("status");
        // Direktsprung aus Org-Cockpit „Im neuen Auftrag verwenden": Positionen
        // vorbefüllen und direkt den Kunden-Picker öffnen.
        const stPositions = location.state?.newOrderPositions;
        if (Array.isArray(stPositions) && stPositions.length) {
            setPrefillPositions(stPositions);
            setPickerInitialTab("existing");
            setCustomerPickerOpen(true);
            if (location.state?.templateName) {
                toast.success(`Vorlage „${location.state.templateName}" wird eingefügt — bitte Kunde wählen.`);
            }
            window.history.replaceState({}, document.title);
        }
        let changed = false;
        const next = new URLSearchParams(searchParams);
        // Jun-2026 (User-Wunsch) — Deep-Links öffnen die Auftragsanlage im passenden Tab.
        const tabForAction = { "new": "existing", "zb1-scan": "scan", "quick-capture": "quick" };
        const presetCustomer = searchParams.get("customer");
        if (action === "new" && presetCustomer) {
            // Jun-2026 (User-Wunsch) — Direktsprung aus CRM-Kundenkarte:
            // Auftrag direkt für diesen Kunden anlegen (Picker überspringen).
            // Optional: ?vehicle=<id> wählt das Garage-Fahrzeug im Modal vor.
            const cust = (customers || []).find((c) => c.id === presetCustomer);
            const label = cust ? `${cust.first_name || ""} ${cust.last_name || cust.company_name || ""}`.trim() : "";
            const presetVehicle = searchParams.get("vehicle");
            setModalOpen({ customer_id: presetCustomer, _customer_label: label, _preselect_vehicle_id: presetVehicle || null });
            next.delete("action"); next.delete("customer"); next.delete("vehicle");
            changed = true;
        } else if (action && tabForAction[action]) {
            setPickerInitialTab(tabForAction[action]);
            setCustomerPickerOpen(true);
            next.delete("action");
            changed = true;
        }
        if (status) {
            setFilterStatus(status);
            next.delete("status");
            changed = true;
        }
        if (changed) setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Jun-2026 (User-Wunsch) — ⌘K „Fahrzeug finden": Deep-Link ?action=new&customer=&vehicle=
    // muss den vorausgefüllten Auftrag auch dann öffnen, wenn die Seite bereits
    // offen ist (kein Remount → der Mount-Effekt oben greift sonst nicht).
    useEffect(() => {
        const action = searchParams.get("action");
        const presetCustomer = searchParams.get("customer");
        if (action !== "new" || !presetCustomer) return;
        const cust = (customers || []).find((c) => c.id === presetCustomer);
        const label = cust ? `${cust.first_name || ""} ${cust.last_name || cust.company_name || ""}`.trim() : "";
        const presetVehicle = searchParams.get("vehicle");
        setModalOpen({ customer_id: presetCustomer, _customer_label: label, _preselect_vehicle_id: presetVehicle || null });
        const next = new URLSearchParams(searchParams);
        next.delete("action"); next.delete("customer"); next.delete("vehicle");
        setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, customers]);

    if (!user) return <div className="p-8">Lade…</div>;
    const custById = Object.fromEntries(customers.map((c) => [c.id, c]));

    // Counts per status
    const counts = orders.reduce((acc, o) => {
        acc[o.status] = (acc[o.status] || 0) + 1;
        return acc;
    }, {});
    counts.all = orders.filter((o) => !["invoiced", "cancelled"].includes(o.status)).length;

    // Jul-2026 (User-Wunsch) — Mitarbeiter-Filter folgt dem gewählten Standort:
    // konkrete Filiale → nur deren Mitarbeiter; „Alle Standorte" → alle
    // Werkstatt-/Kundendienst-Mitarbeiter der Organisation.
    const workshopEmployees = employees.filter((e) => {
        if (!["mechaniker", "kundendienst"].includes(e.employee_function)) return false;
        if (user?.branch_role !== "hauptsitz" || orgBranch === "__all__" || !orgBranch) return true;
        return e.employer_dealer_id === (orgBranch || user?.id);
    });

    // Umsatz je Auftragsart (offene Aufträge, also nicht abgerechnet/storniert).
    // Attribution per Position-Abrechnungskategorie → echter Garantie- vs.
    // Kundenumsatz, auch bei Misch-Aufträgen.
    const posGross = (p) => {
        const net = (Number(p.quantity) || 0) * (Number(p.unit_price_net) || 0) * (1 - (Number(p.discount_percent) || 0) / 100);
        return net * (1 + (Number(p.vat_rate) || 0) / 100);
    };
    const categoryStats = (() => {
        const now = new Date();
        const thisM = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
        const lastM = new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime();
        const s = {};
        ORDER_CATEGORY_LIST.forEach((k) => { s[k] = { sum: 0, count: 0, cur: 0, prev: 0 }; });
        for (const o of orders) {
            if (o.status === "cancelled") continue;
            const hasOwn = !!(o.internal_vehicle_listing_id || o.vehicle?.listing_id);
            const ts = o.created_at ? Date.parse(o.created_at) : 0;
            const open = o.status !== "invoiced";
            if (open) for (const c of orderCategories(o)) s[c.key].count += 1;
            for (const p of o.positions || []) {
                const bc = p.billing_category || "customer";
                const key = bc === "internal" ? (hasOwn ? "gw" : "internal") : (bc === "warranty" ? "warranty" : "customer");
                const g = posGross(p);
                if (open) s[key].sum += g;
                if (ts >= thisM) s[key].cur += g;
                else if (ts >= lastM && ts < thisM) s[key].prev += g;
            }
        }
        ORDER_CATEGORY_LIST.forEach((k) => {
            const { cur, prev } = s[k];
            s[k].trend = prev > 0 ? ((cur - prev) / prev) * 100 : (cur > 0 ? 100 : null);
        });
        return s;
    })();

    // KPI-Kachel „meine Werkstattaufträge" öffnet diese Seite mit ?mine=1 →
    // nur die Aufträge des angemeldeten Verkäufers (angenommen ODER zugewiesen).
    const mineOnly = searchParams.get("mine") === "1";
    // Jul-2026 (User-Wunsch) — Status/Mitarbeiter/24H client-seitig filtern
    // (Chips-Zähler bleiben Gesamt-Zahlen, Umschalten ist sofort).
    let displayedOrders = orders;
    if (filterStatus === "open") displayedOrders = displayedOrders.filter((o) => ["draft", "quote", "confirmed", "in_progress"].includes(o.status));
    else if (filterStatus) displayedOrders = displayedOrders.filter((o) => o.status === filterStatus);
    // Jul-2026 (User-Wunsch): Abgerechnete & stornierte Aufträge NICHT in der
    // Standardliste — nur über die eigenen Filter oder die Universal-Suche.
    else if (!debouncedSearch.trim()) displayedOrders = displayedOrders.filter((o) => !["invoiced", "cancelled"].includes(o.status));
    if (filterEmployee) displayedOrders = displayedOrders.filter((o) => o.employee_id === filterEmployee);
    if (filterOutsideHours) displayedOrders = displayedOrders.filter((o) => o.created_outside_hours);
    if (filterIncomplete) displayedOrders = displayedOrders.filter((o) => o.customer_incomplete);
    if (filterCategory) displayedOrders = displayedOrders.filter((o) => orderCategories(o).some((c) => c.key === filterCategory));
    if (mineOnly && user) {
        displayedOrders = displayedOrders.filter(
            (o) => o.accepted_by_id === user.id || (user.name && o.employee === user.name),
        );
    }
    const clearMine = () => {
        const next = new URLSearchParams(searchParams);
        next.delete("mine");
        setSearchParams(next, { replace: true });
    };

    // Jul-2026 (User-Wunsch) — Standard-Sortierung nach AUFTRAGSNUMMER:
    // der jüngste Auftrag (höchste Nummer) steht immer ganz oben.
    const orderNumKey = (o) => {
        const m = /WO-(\d{4})-(\d+)/.exec(o.order_number || "");
        return m ? Number(m[1]) * 1000000 + Number(m[2]) : 0;
    };
    displayedOrders = [...displayedOrders].sort((a, b) => {
        const d = orderNumKey(b) - orderNumKey(a);
        if (d) return d;
        return Date.parse(b.created_at || 0) - Date.parse(a.created_at || 0);
    });

    return (
        <div className="min-h-screen bg-gray-50" data-testid="workshop-orders-page">
            <EmployeeContextBanner />
            <div className="bg-[#111827] text-white">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
                    <div className="flex items-start justify-between gap-6 mt-4">
                        <div className="flex-1 min-w-0">
                            <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight inline-flex items-center gap-2">Werkstatt-Aufträge <SettingsShortcut to="/dashboard?tab=settings#settings-werkstatt" title="Werkstatt-Einstellungen" testid="settings-shortcut-werkstatt" dark /></h1>
                            {mineOnly && (
                                <button
                                    onClick={clearMine}
                                    className="inline-flex items-center gap-1.5 mt-3 bg-[#FFCC00] text-[#111827] text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm hover:bg-[#E6B800]"
                                    data-testid="workshop-mine-filter-chip"
                                >
                                    Nur meine Aufträge · {displayedOrders.length} <span className="text-base leading-none">×</span>
                                </button>
                            )}
                            <p className="text-sm text-gray-400 mt-2 truncate max-w-full">
                                Angebot → Auftrag → Abrechnung — Lagerteile werden bei Fertigstellung automatisch abgebucht.
                            </p>
                        </div>
                    </div>
                    {/* Jun-2026 (User-Wunsch): QR ganz rechts, links davon die zwei Button-Zeilen */}
                    <div className="flex items-end justify-end gap-3 md:gap-4 mt-5">
                        <div className="flex-1 flex flex-col items-end gap-3 min-w-0">
                    {/* Zeile 1: Navigation (Routing) — rechtsbündig */}
                    <div className="flex gap-2 flex-wrap items-center justify-end max-w-full" data-testid="wo-nav-row">
                                {isEmployeeContext ? (
                                    <Button onClick={exitEmployerContext} variant="outline" className="bg-transparent border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white rounded-sm text-xs uppercase tracking-wider" data-testid="back-to-dashboard">
                                        ← Zurück zum Firmen-Bereich
                                    </Button>
                                ) : (
                                    <Button onClick={() => navigate("/dashboard")} variant="outline" className="bg-transparent border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white rounded-sm text-xs uppercase tracking-wider" data-testid="back-to-dashboard">
                                        ← Dashboard
                                    </Button>
                                )}
                                <Link to="/dealer/wms" className="text-xs uppercase tracking-wider text-gray-300 hover:text-white border border-gray-600 px-3 py-2 rounded-sm" data-testid="link-wms">
                                    ← Warenwirtschaft
                                </Link>
                                <Link to="/dealer/workshop-calendar" className="text-xs uppercase tracking-wider text-gray-300 hover:text-white border border-gray-600 px-3 py-2 rounded-sm" data-testid="link-workshop-calendar">
                                    Kalender →
                                </Link>
                                {/* Jun-2026 (User-Wunsch): Winterlager gehört zu Werkstatt-Aufträgen, nicht in die WWS. */}
                                <Link to="/dealer/winterlager" className="text-xs uppercase tracking-wider text-cyan-300 hover:text-white border border-cyan-700 px-3 py-2 rounded-sm" data-testid="link-winterlager">
                                    ❄ Winterlager →
                                </Link>
                                <Link to="/dealer/workshop-orders/shared-with-me" className="text-xs uppercase tracking-wider text-gray-300 hover:text-white border border-gray-600 px-3 py-2 rounded-sm" data-testid="link-shared-orders">
                                    Geteilte Aufträge →
                                </Link>
                                <Link to="/dealer/invoices" className="text-xs uppercase tracking-wider text-gray-300 hover:text-white border border-gray-600 px-3 py-2 rounded-sm" data-testid="link-invoices">
                                    Rechnungen →
                                </Link>
                            </div>
                            {/* Zeile 2: Aktionen dieser Seite — rechtsbündig */}
                            <div className="flex gap-2 flex-wrap items-center justify-end max-w-full" data-testid="wo-action-row">
                                <WorkshopBranchSwitcher user={user} value={orgBranch} onChange={setOrgBranch} />
                                <Button onClick={() => { setPickerInitialTab("quick"); setCustomerPickerOpen(true); }} variant="outline" className="bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600 font-bold rounded-sm" data-testid="wo-quick-capture-btn">
                                    <Zap className="w-4 h-4 mr-1.5" /> Schnell-Erfassung
                                </Button>
                                <Button onClick={() => { setPickerInitialTab("scan"); setCustomerPickerOpen(true); }} variant="outline" className="bg-purple-600 hover:bg-purple-700 text-white border-purple-600 font-bold rounded-sm" data-testid="wo-zb1-scan-btn">
                                    <Camera className="w-4 h-4 mr-1.5" /> Scan-Erfassung
                                </Button>
                                <Button onClick={() => { setPickerInitialTab("existing"); setCustomerPickerOpen(true); }} className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm" data-testid="wo-create-btn">
                                    <Plus className="w-4 h-4 mr-1.5" /> Bestands Kunde / Fahrzeug
                                </Button>
                            </div>
                        </div>
                        <div className="shrink-0" data-testid="wo-header-qr">
                            <MobileZb1QrCard key={orgBranch || "main"} variant="header" onScanned={handleHandoffBundle} />
                        </div>
                    </div>
                </div>
            </div>

            {orgBranch && (
                <div className="max-w-7xl mx-auto px-4 md:px-8 mt-4">
                    <div className="bg-[#FFCC00]/15 border border-[#FFCC00] rounded-sm px-4 py-2.5 flex items-center gap-2 text-sm" data-testid="workshop-branch-banner">
                        <Building2 className="w-4 h-4 text-[#111827]" />
                        {orgBranch === "__all__" ? (
                            <>
                                <span className="font-bold text-[#111827]">Alle Standorte (Aggregat)</span>
                                <span className="text-gray-600">— du siehst die Werkstattaufträge aller Standorte der Organisation.</span>
                            </>
                        ) : (
                            <>
                                <span className="font-bold text-[#111827]">Filial-Werkstatt-Ansicht aktiv</span>
                                <span className="text-gray-600">— du siehst die Werkstattaufträge dieser Filiale. Zum Hauptsitz zurück: „★"-Eintrag im Umschalter wählen.</span>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Aug-2026 (User-Wunsch): Board — geteilte Aufträge + Antwort-Status */}
            <SharedOrdersBoard onOpenOrder={(id) => { const o = orders.find((x) => x.id === id); if (o) setModalOpen(o); }} />

            {/* Phase 4c++ — Tabs: Aufträge + Eingehende Buchungen */}
            <div className="max-w-7xl mx-auto px-4 md:px-8 mt-6">
                <div className="flex gap-0 border-b border-gray-200 overflow-x-auto whitespace-nowrap [&>button]:shrink-0" data-testid="wo-tabs">
                    <button
                        type="button"
                        onClick={() => setActiveTab("orders")}
                        className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors ${activeTab === "orders" ? "border-[#111827] text-[#111827]" : "border-transparent text-gray-500 hover:text-gray-800"}`}
                        data-testid="wo-tab-orders"
                    >
                        Aufträge <span className="ml-1 text-[10px] text-gray-500 font-mono-num">({orders.length})</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab("rental")}
                        className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${activeTab === "rental" ? "border-teal-500 text-[#111827]" : "border-transparent text-gray-500 hover:text-gray-800"}`}
                        data-testid="wo-tab-rental"
                    >
                        <Car className="w-3.5 h-3.5" /> Ersatz-Fahrzeug
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab("delivery-open")}
                        className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${activeTab === "delivery-open" ? "border-amber-500 text-[#111827]" : "border-transparent text-gray-500 hover:text-gray-800"}`}
                        data-testid="wo-tab-delivery-open"
                    >
                        <Truck className="w-3.5 h-3.5" /> Offene Auslieferung
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab("warranty-open")}
                        className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${activeTab === "warranty-open" ? "border-rose-500 text-[#111827]" : "border-transparent text-gray-500 hover:text-gray-800"}`}
                        data-testid="wo-tab-warranty-open"
                    >
                        <ShieldCheck className="w-3.5 h-3.5" /> Offene Garantien
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab("customers")}
                        className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${activeTab === "customers" ? "border-[#7C3AED] text-[#111827]" : "border-transparent text-gray-500 hover:text-gray-800"}`}
                        data-testid="wo-tab-customers"
                    >
                        <Users className="w-3.5 h-3.5" /> Kunden
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab("vehicles")}
                        className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${activeTab === "vehicles" ? "border-blue-500 text-[#111827]" : "border-transparent text-gray-500 hover:text-gray-800"}`}
                        data-testid="wo-tab-vehicles"
                    >
                        <Bike className="w-3.5 h-3.5" /> Fahrzeuge
                    </button>
                </div>
            </div>

            {activeTab === "rental" ? (
                <div className="max-w-7xl mx-auto px-4 md:px-8 mt-6 pb-16">
                    <RentalVehiclesTab />
                </div>
            ) : activeTab === "delivery-open" ? (
                <div className="max-w-7xl mx-auto px-4 md:px-8 mt-6 pb-16">
                    <OpenDeliveryOrdersTab />
                </div>
            ) : activeTab === "warranty-open" ? (
                <div className="max-w-7xl mx-auto px-4 md:px-8 mt-6 pb-16">
                    <OpenWarrantyOrdersTab />
                </div>
            ) : activeTab === "customers" ? (
                <div className="max-w-7xl mx-auto px-4 md:px-8 mt-6 pb-16">
                    <CustomersPanel />
                </div>
            ) : activeTab === "vehicles" ? (
                <div className="max-w-7xl mx-auto px-4 md:px-8 mt-6 pb-16">
                    <VehiclesPanel />
                </div>
            ) : (
            <>
            {/* Phase 4g.Q — Universal-Suchfeld (Kunden + Fahrzeug + Auftragsdaten) */}
            <div className="max-w-7xl mx-auto px-4 md:px-8 mt-6">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-600" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Suche über Kunde, Fahrzeug, Kennzeichen, FIN, Auftragsnr. …"
                        className="pl-9 rounded-sm border-2 border-emerald-500 placeholder:text-emerald-700/60 focus-visible:ring-emerald-500"
                        data-testid="wo-universal-search"
                    />
                    {search && (
                        <button
                            type="button"
                            onClick={() => setSearch("")}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                            data-testid="wo-universal-search-clear"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>
            {/* Status filter chips */}
            <div className="max-w-7xl mx-auto px-4 md:px-8 mt-6">
                <div className="flex gap-1.5 flex-wrap" data-testid="wo-filter-chips">
                    {[{ k: "", l: "Alle" }, ...Object.entries(STATUS_META).filter(([k]) => !["invoiced", "cancelled", "draft"].includes(k)).map(([k, m]) => ({ k, l: m.label }))].map((f) => (
                        <button
                            key={f.k || "all"}
                            type="button"
                            onClick={() => { setFilterStatus(f.k); setActiveTab("orders"); }}
                            className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm border transition-colors inline-flex items-center gap-1.5 ${activeTab === "orders" && filterStatus === f.k ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}
                            data-testid={`wo-filter-${f.k || "all"}`}
                        >
                            {f.l}
                            <span className={`text-[10px] px-1.5 rounded-sm ${activeTab === "orders" && filterStatus === f.k ? "bg-white/20" : "bg-gray-100"}`}>
                                {f.k ? (counts[f.k] || 0) : counts.all}
                            </span>
                        </button>
                    ))}
                    <button
                        type="button"
                        onClick={() => { setFilterStatus("invoiced"); setActiveTab("orders"); }}
                        className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm border inline-flex items-center gap-1.5 ${activeTab === "orders" && filterStatus === "invoiced" ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50"}`}
                        data-testid="wo-filter-invoiced"
                    >
                        Abgerechnet
                        <span className="text-[10px] px-1.5 rounded-sm bg-gray-100 text-gray-700">{counts.invoiced || 0}</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => { setFilterStatus("cancelled"); setActiveTab("orders"); }}
                        title="Stornierte Aufträge"
                        className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm border inline-flex items-center gap-1.5 ${activeTab === "orders" && filterStatus === "cancelled" ? "bg-red-700 text-white border-red-700" : "bg-white text-red-700 border-red-300 hover:bg-red-50"}`}
                        data-testid="wo-filter-cancelled"
                    >
                        Storniert
                        <span className="text-[10px] px-1.5 rounded-sm bg-gray-100 text-gray-700">{counts.cancelled || 0}</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => { setActiveTab("orders"); setFilterIncomplete((v) => !v); }}
                        title="Aufträge, bei denen Kundendaten fehlen (kein Kunde, keine Kontaktdaten oder kein Fahrzeug) — bitte Stammdaten ergänzen"
                        className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm border inline-flex items-center gap-1.5 ${activeTab === "orders" && filterIncomplete ? "bg-orange-600 text-white border-orange-600" : "bg-white text-orange-700 border-orange-300 hover:bg-orange-50"}`}
                        data-testid="wo-filter-incomplete"
                    >
                        Kundendaten unvollständig
                        <span className="text-[10px] px-1.5 rounded-sm bg-gray-100 text-gray-700">{orders.filter((o) => o.customer_incomplete).length}</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => { setActiveTab("orders"); setFilterOutsideHours((v) => !v); }}
                        title="Auftragseingänge der 24/7-Selbst-Annahme (außerhalb der Öffnungszeiten erstellt, z. B. nachts oder am Wochenende)"
                        className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm border inline-flex items-center gap-1.5 ${activeTab === "orders" && filterOutsideHours ? "bg-indigo-700 text-white border-indigo-700" : "bg-white text-indigo-700 border-indigo-300 hover:bg-indigo-50"}`}
                        data-testid="wo-filter-outside-hours"
                    >
                        <Clock className="w-3.5 h-3.5" /> 24H Selbst-Annahme
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab("bookings")}
                        title="Eingehende Online-Buchungen über die öffentliche Werkstatt-Terminbuchung"
                        className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm border inline-flex items-center gap-1.5 ${activeTab === "bookings" ? "bg-[#FFCC00] text-[#111827] border-[#FFCC00]" : "bg-white text-[#111827] border-amber-300 hover:bg-amber-50"}`}
                        data-testid="wo-filter-online-booking"
                    >
                        <CalendarCheck className="w-3.5 h-3.5" /> Online-Buchung
                        {bookings.filter((b) => b.status === "confirmed").length > 0 && (
                            <span className="bg-[#111827] text-[#FFCC00] text-[10px] font-mono-num px-1.5 rounded-sm" data-testid="wo-filter-online-booking-badge">
                                {bookings.filter((b) => b.status === "confirmed").length} neu
                            </span>
                        )}
                        <span className="text-[10px] px-1.5 rounded-sm bg-gray-100 text-gray-700">{bookings.length}</span>
                    </button>
                    {(filterStatus || filterEmployee || filterOutsideHours || filterCategory || filterIncomplete || search) && (
                        <button
                            type="button"
                            onClick={() => { setFilterStatus(""); setFilterEmployee(""); setFilterOutsideHours(false); setFilterCategory(""); setFilterIncomplete(false); setSearch(""); }}
                            className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 inline-flex items-center gap-1.5"
                            data-testid="wo-filter-reset-all"
                        >
                            <X className="w-3.5 h-3.5" /> Alle Filter zurücksetzen
                        </button>
                    )}
                </div>
                {workshopEmployees.length > 0 && (
                    <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="wo-filter-employee-wrap">
                        <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Mitarbeiter:</label>
                        <select
                            value={filterEmployee}
                            onChange={(e) => setFilterEmployee(e.target.value)}
                            className="text-xs rounded-sm border border-gray-300 px-2 py-1 bg-white"
                            data-testid="wo-filter-employee"
                            aria-label="Nach Mitarbeiter filtern"
                        >
                            <option value="">— Alle —</option>
                            {workshopEmployees.map((e) => (
                                <option key={e.id} value={e.id}>
                                    {e.name}{e.employee_function ? ` · ${e.employee_function}` : ""}{e.mechanic_specialty ? ` (${e.mechanic_specialty})` : ""}{orgBranch === "__all__" && e.site_code ? ` — ${e.site_code}` : ""}
                                </option>
                            ))}
                        </select>
                        {filterEmployee && (
                            <button onClick={() => setFilterEmployee("")} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider rounded-sm border border-red-300 bg-red-50 text-red-700 hover:bg-red-100" data-testid="wo-filter-employee-clear">
                                <X className="w-3.5 h-3.5" /> zurücksetzen
                            </button>
                        )}
                    </div>
                )}
            </div>

            {activeTab === "bookings" ? (
            <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
                <BookingsTab
                    bookings={bookings}
                    onConvert={convertBookingToOrder}
                    onUpdateStatus={updateBookingStatus}
                    onRefresh={loadAll}
                />
            </div>
            ) : (
            <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
                {/* Umsatz-Kacheln je Auftragsart (offen) — zugleich Filter (Jun 2026) */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4" data-testid="wo-category-tiles">
                    {ORDER_CATEGORY_LIST.map((k) => {
                        const c = ORDER_CATEGORY_META[k];
                        const st = categoryStats[k] || { sum: 0, count: 0 };
                        const active = filterCategory === k;
                        return (
                            <button
                                key={k}
                                type="button"
                                onClick={() => setFilterCategory(active ? "" : k)}
                                className={`text-left bg-white border rounded-sm p-3 transition-colors relative overflow-hidden ${active ? "border-[#111827] ring-2 ring-[#111827]/20" : "border-gray-200 hover:border-gray-400"}`}
                                data-testid={`wo-category-tile-${k}`}
                            >
                                <span className="absolute left-0 top-0 bottom-0 w-1.5" style={{ backgroundColor: c.hex }} />
                                <div className="pl-2">
                                    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold flex items-center gap-1.5">
                                        <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: c.hex }} />
                                        {c.label}
                                    </div>
                                    <div className="flex items-baseline gap-2">
                                        <div className="text-lg font-black font-mono-num mt-0.5" data-testid={`wo-category-sum-${k}`}>{fmtEur(st.sum)}</div>
                                        {st.trend !== null && st.trend !== undefined && (
                                            <span
                                                className={`text-[11px] font-bold ${st.trend > 0 ? "text-emerald-600" : st.trend < 0 ? "text-red-600" : "text-gray-400"}`}
                                                title="Umsatz-Trend zum Vormonat (erfasste Aufträge)"
                                                data-testid={`wo-category-trend-${k}`}
                                            >
                                                {st.trend > 0 ? "▲" : st.trend < 0 ? "▼" : "→"} {Math.abs(Math.round(st.trend))}%
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-[10px] text-gray-400">{st.count} offene{st.count === 1 ? "r" : ""} Auftrag{st.count === 1 ? "" : "e"} · offener Umsatz</div>
                                </div>
                            </button>
                        );
                    })}
                </div>
                {filterCategory && (
                    <div className="flex items-center gap-2 mb-3 text-xs" data-testid="wo-category-filter-active">
                        <span className="text-gray-500">Gefiltert nach:</span>
                        <span className="inline-flex items-center gap-1.5 font-bold bg-gray-100 border border-gray-300 rounded-sm px-2 py-1">
                            <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: (ORDER_CATEGORY_META[filterCategory] || {}).hex }} />
                            {(ORDER_CATEGORY_META[filterCategory] || {}).label}
                        </span>
                        <button type="button" onClick={() => setFilterCategory("")} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider rounded-sm border border-red-300 bg-red-50 text-red-700 hover:bg-red-100" data-testid="wo-category-filter-clear">
                            <X className="w-3.5 h-3.5" /> zurücksetzen
                        </button>
                        <span className="text-gray-400">· Misch-Aufträge zählen in jede enthaltene Kategorie</span>
                    </div>
                )}
                {loading ? (
                    <div className="text-center py-12 text-gray-500">Lade…</div>
                ) : orders.length === 0 ? (
                    <div className="bg-white border border-gray-200 rounded-sm p-12 text-center" data-testid="wo-empty">
                        <Wrench className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <div className="font-display font-black text-lg">Noch keine Aufträge</div>
                        <p className="text-sm text-gray-500 mt-1">Legen Sie Ihren ersten Werkstatt-Auftrag an.</p>
                    </div>
                ) : displayedOrders.length === 0 ? (
                    <div className="bg-white border border-gray-200 rounded-sm p-10 text-center" data-testid="wo-empty-filtered">
                        <div className="font-bold text-gray-600">Keine Aufträge in dieser Auftragsart</div>
                        <button type="button" onClick={() => setFilterCategory("")} className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm border border-red-300 bg-red-50 text-red-700 hover:bg-red-100" data-testid="wo-category-filter-reset-2"><X className="w-3.5 h-3.5" /> Filter zurücksetzen</button>
                    </div>
                ) : (
                    <div className="space-y-2" data-testid="wo-list">
                        {displayedOrders.map((o) => (
                            <OrderCard
                                key={o.id}
                                order={o}
                                customer={custById[o.customer_id] || o.customer_brief}
                                highlighted={o.id === highlightId}
                                onEdit={() => setModalOpen(o)}
                                onDelete={() => remove(o.id)}
                                onTransition={(s, note) => transition(o.id, s, note)}
                                onConvert={() => openInvoicePreview(o)}
                                onQuotePdf={() => downloadQuotePdf(o)}
                                onQuoteMail={() => setMailTarget({ order: o, customer: custById[o.customer_id] || o.customer_brief })}
                                onQrSticker={() => downloadQrSticker(o)}
                                onPrintOrder={() => printOrder(o)}
                                onQrPreview={() => reprintBundle(o)}
                                onShare={() => setShareTarget(o)}
                                onTimer={() => setTimerTarget(o)}
                                onSendReminder={sendReminderEmail}
                                onPlanCalendar={planInCalendar}
                                onNotifyDelay={notifyDelay}
                            />
                        ))}
                    </div>
                )}
            </div>
            )}
            </>
            )}

            {modalOpen && (
                <WorkOrderModal
                    order={modalOpen}
                    customers={customers}
                    articles={articles}
                    employees={employees}
                    onClose={() => setModalOpen(null)}
                    onBack={!modalOpen.id ? () => { setModalOpen(null); setCustomerPickerOpen(true); } : undefined}
                    onSaved={() => { setModalOpen(null); loadAll(); }}
                    onDirectInvoice={(ord) => { setModalOpen(null); openInvoicePreview(ord); }}
                />
            )}

            {customerPickerOpen && (
                <CustomerPickerForOrder
                    customers={customers}
                    initialTab={pickerInitialTab}
                    handoffBundle={pickerPreloadedScan}
                    onClose={() => { setCustomerPickerOpen(false); setPickerPreloadedScan(null); }}
                    onPicked={(initialDraft) => {
                        setCustomerPickerOpen(false);
                        setPickerPreloadedScan(null);
                        // Trigger reload to refresh customers list, then open modal
                        loadAll().then(() => setModalOpen({
                            ...(initialDraft || {}),
                            ...(prefillPositions ? { positions: prefillPositions } : {}),
                        }));
                        setPrefillPositions(null);
                    }}
                />
            )}

            {mailTarget && (
                <SendQuoteMailModal
                    order={mailTarget.order}
                    customer={mailTarget.customer}
                    onClose={() => setMailTarget(null)}
                    onSent={() => { setMailTarget(null); loadAll(); }}
                />
            )}

            {qrTarget && (
                <QrPreviewModal
                    order={qrTarget}
                    onClose={() => setQrTarget(null)}
                    onDownload={() => downloadQrSticker(qrTarget)}
                />
            )}

            {shareTarget && (
                <ShareOrderModal
                    order={shareTarget}
                    onClose={() => setShareTarget(null)}
                    onShared={() => { setShareTarget(null); loadAll(); }}
                />
            )}

            {timerTarget && (
                <TimesheetModal
                    order={timerTarget}
                    employees={employees}
                    onClose={() => setTimerTarget(null)}
                    onUpdated={loadAll}
                />
            )}

            {invoicePreview && (
                <InvoicePreviewModal
                    order={invoicePreview}
                    customer={custById[invoicePreview.customer_id] || invoicePreview.customer_brief}
                    busy={billingBusy}
                    onClose={() => setInvoicePreview(null)}
                    onEdit={() => { const o = invoicePreview; setInvoicePreview(null); setModalOpen(o); }}
                    onConfirm={(mode, freshOrder, voucherCode, customTotalGross, paymentCode) => performBilling(freshOrder || invoicePreview, mode, voucherCode, customTotalGross, paymentCode)}
                />
            )}
        </div>
    );
}

// OrderCard wurde nach components/workshop/OrderCard.jsx extrahiert (Phase 4g.C).

// WorkOrderModal + PositionRow + DamagePhotoSection wurden nach
// components/workshop/WorkOrderModal.jsx extrahiert (Phase 4g.C+).



// SendQuoteMailModal, QrPreviewModal, ShareOrderModal, BookingsTab & BookingCard
// wurden nach components/workshop/* extrahiert (Phase 4g.E).
